import React from 'react';
import { CheckCircle2, CircleAlert, Loader2, CircleDashed } from 'lucide-react';
import { describeDeploymentStatus, type Deployment, type DeploymentTone } from './deploymentStatus';

const TONE_CLASSES: Record<DeploymentTone, string> = {
  neutral: 'bg-zinc-100 text-zinc-600',
  progress: 'bg-amber-100 text-amber-800',
  success: 'bg-emerald-100 text-emerald-800',
  error: 'bg-red-100 text-red-800',
};

const TONE_ICON: Record<DeploymentTone, React.ReactNode> = {
  neutral: <CircleDashed size={13} aria-hidden />,
  progress: <Loader2 size={13} className="animate-spin" aria-hidden />,
  success: <CheckCircle2 size={13} aria-hidden />,
  error: <CircleAlert size={13} aria-hidden />,
};

interface Props {
  deployment: Deployment | null;
  /** Hide entirely until the first status load resolves, to avoid a flash. */
  loading?: boolean;
}

/**
 * Compact, truthful deployment-status indicator for the editor toolbar.
 * Shows exactly the backend-reported state; when there is no deployment record
 * it reads "Status unavailable" instead of implying the site is live.
 */
const DeploymentStatusPill: React.FC<Props> = ({ deployment, loading }) => {
  if (loading && !deployment) return null;
  const display = describeDeploymentStatus(deployment);
  return (
    <span
      role="status"
      aria-live="polite"
      title={display.detail ?? display.label}
      className={`hidden items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium sm:inline-flex ${TONE_CLASSES[display.tone]}`}
    >
      {TONE_ICON[display.tone]}
      <span>{display.label}</span>
    </span>
  );
};

export default DeploymentStatusPill;
